import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, LogOut, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

/**
 * Tarjeta de usuario al pie del sidebar: avatar con iniciales, nombre,
 * chip de rol y popover con "Perfil" y "Cerrar sesión".
 */
export default function SidebarUserCard({ user, collapsed, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials =
    (`${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.trim() ||
      user?.email?.[0] ||
      "?").toUpperCase();
  const fullName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email;
  const roleLabel =
    user?.role === "owner" ? t("common.role_owner") : t("common.role_tenant");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`w-full flex items-center gap-3 rounded-lg hover:bg-stone-100 transition-colors text-left ${
            collapsed ? "justify-center p-2" : "p-2.5"
          }`}
          data-testid="sidebar-user-card"
        >
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
            style={{ background: "var(--brand-fill)" }}
            data-testid="user-card-avatar"
          >
            {initials}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-stone-900 truncate" data-testid="user-card-name">
                  {fullName}
                </div>
                <span className="inline-block text-xs text-stone-500" data-testid="user-card-role">
                  {roleLabel}
                </span>
              </div>
              <ChevronsUpDown className="w-4 h-4 text-stone-500 shrink-0" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={collapsed ? "right" : "top"}
        align={collapsed ? "end" : "start"}
        className="w-52 p-1.5"
        data-testid="user-card-popover"
      >
        <button
          onClick={() => {
            setOpen(false);
            navigate("/profile");
          }}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-100 transition-colors"
          data-testid="user-card-profile-link"
        >
          <User className="w-4 h-4" />
          {t("more.profile")}
        </button>
        <button
          onClick={() => {
            setOpen(false);
            onLogout();
          }}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          data-testid="desktop-logout-button"
        >
          <LogOut className="w-4 h-4" />
          {t("common.logout")}
        </button>
      </PopoverContent>
    </Popover>
  );
}
